import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  courseService,
  assignmentService,
  quizService,
} from '../services/apiService'

const CourseDetail = () => {
  const { id } = useParams()
  const [course, setCourse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [enrolling, setEnrolling] = useState(false)
  const [isEnrolled, setIsEnrolled] = useState(false)
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [activeTab, setActiveTab] = useState('lessons')
  const [assignments, setAssignments] = useState([])
  const [quizzes, setQuizzes] = useState([])
  const [userProgress, setUserProgress] = useState(null)
  const [loadingProgress, setLoadingProgress] = useState(false)

  const fetchCourse = useCallback(async () => {
    try {
      const response = await courseService.getCourse(id)
      setCourse(response.data)
    } catch (error) {
      console.error('Error fetching course:', error)
    } finally {
      setLoading(false)
    }
  }, [id])

  const checkEnrollment = useCallback(async () => {
    try {
      const response = await courseService.getEnrollments()
      const data = response.data.results || response.data
      const enrollments = Array.isArray(data) ? data : []
      const enrolled = enrollments.some((e) => e.course.id === parseInt(id, 10))
      setIsEnrolled(enrolled)
    } catch (error) {
      console.error('Error checking enrollment:', error)
    }
  }, [id])

  const handleEnroll = async () => {
    setEnrolling(true)
    try {
      await courseService.enrollCourse(id)
      setIsEnrolled(true)
      alert('Successfully enrolled in the course!')
    } catch (error) {
      console.error('Error enrolling:', error)
      alert('Failed to enroll. Please try again.')
    } finally {
      setEnrolling(false)
    }
  }

  const fetchAssignments = useCallback(async () => {
    try {
      const response = await assignmentService.getCourseAssignments(id)
      const data = response.data.results || response.data
      setAssignments(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching assignments:', error)
    }
  }, [id])

  const fetchQuizzes = useCallback(async () => {
    try {
      const response = await quizService.getCourseQuizzes(id)
      const data = response.data.results || response.data
      setQuizzes(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching quizzes:', error)
    }
  }, [id])

  const fetchUserProgress = useCallback(async () => {
    try {
      const response = await courseService.getUserProgress(id)
      setUserProgress(response.data)
    } catch (error) {
      console.error('Error fetching progress:', error)
    }
  }, [id])

  const markLessonComplete = async () => {
    if (!selectedLesson) return
    setLoadingProgress(true)
    try {
      await courseService.markLessonComplete(id, selectedLesson.id)
      // Refresh progress data
      await fetchUserProgress()
      alert('Lesson marked as complete!')
    } catch (error) {
      console.error('Error marking lesson complete:', error)
      alert('Failed to mark lesson complete. Please try again.')
    } finally {
      setLoadingProgress(false)
    }
  }

  useEffect(() => {
    fetchCourse()
    checkEnrollment()
  }, [fetchCourse, checkEnrollment])

  useEffect(() => {
    if (isEnrolled) {
      fetchAssignments()
      fetchQuizzes()
      fetchUserProgress()
    }
  }, [isEnrolled, fetchAssignments, fetchQuizzes, fetchUserProgress])

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <p className='text-center text-gray-500'>Course not found.</p>
      </div>
    )
  }

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <div className='bg-white rounded-lg shadow-lg overflow-hidden'>
        {course.thumbnail ? (
          <img
            src={course.thumbnail}
            alt={course.title}
            className='w-full h-64 object-cover'
          />
        ) : (
          <div className='w-full h-64 bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center'>
            <span className='text-white text-6xl font-bold'>
              {course.title.charAt(0)}
            </span>
          </div>
        )}

        <div className='p-8'>
          <div className='flex items-center justify-between mb-4'>
            <h1 className='text-3xl font-bold text-gray-900'>{course.title}</h1>
            {!isEnrolled && (
              <button
                onClick={handleEnroll}
                disabled={enrolling}
                className='bg-primary-600 text-white px-6 py-3 rounded-md font-medium hover:bg-primary-700 disabled:opacity-50'
              >
                {enrolling ? 'Enrolling...' : 'Enroll Now'}
              </button>
            )}
            {isEnrolled && (
              <span className='bg-green-100 text-green-800 px-4 py-2 rounded-md font-medium'>
                Enrolled
              </span>
            )}
          </div>

          <div className='flex items-center text-gray-600 mb-6'>
            <span className='mr-4'>
              By {course.instructor.first_name} {course.instructor.last_name}
            </span>
            <span className='mr-4'>• {course.total_lessons} lessons</span>
            <span>• {course.total_duration} minutes</span>
          </div>

          {/* Progress Bar for enrolled students */}
          {isEnrolled && userProgress && (
            <div className='mb-6'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium text-gray-700'>
                  Your Progress
                </span>
                <span className='text-sm font-bold text-primary-600'>
                  {userProgress.overall_progress}%
                </span>
              </div>
              <div className='w-full bg-gray-200 rounded-full h-2.5'>
                <div
                  className='bg-primary-600 h-2.5 rounded-full transition-all duration-300'
                  style={{ width: `${userProgress.overall_progress}%` }}
                ></div>
              </div>
            </div>
          )}

          <div className='prose max-w-none mb-8'>
            <h2 className='text-xl font-semibold mb-4'>About this course</h2>
            <p className='text-gray-700'>{course.description}</p>
          </div>

          {/* Tabs for enrolled students */}
          {isEnrolled && (
            <div className='border-b border-gray-200 mb-6'>
              <nav className='-mb-px flex space-x-8'>
                <button
                  onClick={() => setActiveTab('lessons')}
                  className={`${
                    activeTab === 'lessons'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Lessons ({course.total_lessons || 0})
                </button>
                <button
                  onClick={() => setActiveTab('assignments')}
                  className={`${
                    activeTab === 'assignments'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Assignments ({assignments.length})
                </button>
                <button
                  onClick={() => setActiveTab('quizzes')}
                  className={`${
                    activeTab === 'quizzes'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Quizzes ({quizzes.length})
                </button>
              </nav>
            </div>
          )}

          {/* Lessons Tab */}
          {(activeTab === 'lessons' || !isEnrolled) &&
            course.lessons &&
            course.lessons.length > 0 && (
              <div>
                <h2 className='text-xl font-semibold mb-4'>Course Content</h2>
                <div className='space-y-2'>
                  {course.lessons.map((lesson, index) => (
                    <div
                      key={lesson.id}
                      className={`border border-gray-200 rounded-md p-4 ${
                        isEnrolled
                          ? 'hover:bg-gray-50 cursor-pointer'
                          : 'opacity-75'
                      }`}
                      onClick={() => isEnrolled && setSelectedLesson(lesson)}
                    >
                      <div className='flex items-center justify-between'>
                        <div className='flex-1'>
                          <div className='flex items-center gap-2'>
                            <h3 className='font-medium text-gray-900'>
                              {index + 1}. {lesson.title}
                            </h3>
                            {isEnrolled &&
                              userProgress &&
                              userProgress.lessons.completed_ids.includes(
                                lesson.id,
                              ) && (
                                <span className='inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium'>
                                  ✓ Completed
                                </span>
                              )}
                          </div>
                          <p className='text-sm text-gray-500'>
                            {lesson.duration} minutes
                          </p>
                        </div>
                        {isEnrolled ? (
                          <svg
                            className='h-5 w-5 text-gray-400'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M9 5l7 7-7 7'
                            />
                          </svg>
                        ) : (
                          <svg
                            className='h-5 w-5 text-gray-400'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {!isEnrolled && (
                  <p className='text-sm text-gray-500 mt-4'>
                    Enroll in this course to access all lessons
                  </p>
                )}
              </div>
            )}

          {/* Assignments Tab */}
          {isEnrolled && activeTab === 'assignments' && (
            <div>
              <h2 className='text-xl font-semibold mb-4'>Course Assignments</h2>
              {assignments.length > 0 ? (
                <div className='space-y-3'>
                  {assignments.map((assignment) => (
                    <Link
                      key={assignment.id}
                      to={`/assignments/${assignment.id}`}
                      className='block border border-gray-200 rounded-md p-4 hover:bg-gray-50'
                    >
                      <div className='flex items-start justify-between'>
                        <div className='flex-1'>
                          <h3 className='font-medium text-gray-900'>
                            {assignment.title}
                          </h3>
                          <p className='text-sm text-gray-600 mt-1 line-clamp-2'>
                            {assignment.description}
                          </p>
                          <p className='text-sm text-gray-500 mt-2'>
                            Due:{' '}
                            {new Date(assignment.due_date).toLocaleString()}
                          </p>
                        </div>
                        <svg
                          className='h-5 w-5 text-gray-400 ml-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M9 5l7 7-7 7'
                          />
                        </svg>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className='text-gray-500 text-center py-8'>
                  No assignments available yet
                </p>
              )}
            </div>
          )}

          {/* Quizzes Tab */}
          {isEnrolled && activeTab === 'quizzes' && (
            <div>
              <h2 className='text-xl font-semibold mb-4'>Course Quizzes</h2>
              {quizzes.length > 0 ? (
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  {quizzes.map((quiz) => (
                    <Link
                      key={quiz.id}
                      to={`/quizzes/${quiz.id}/take`}
                      className='border border-gray-200 rounded-md p-4 hover:bg-gray-50'
                    >
                      <h3 className='font-medium text-gray-900 mb-2'>
                        {quiz.title}
                      </h3>
                      <p className='text-sm text-gray-600 mb-3 line-clamp-2'>
                        {quiz.description}
                      </p>
                      <div className='flex items-center justify-between text-sm text-gray-500'>
                        <span>{quiz.time_limit} min</span>
                        <span>{quiz.total_questions || 0} questions</span>
                        <span className='text-primary-600 font-medium'>
                          Start →
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className='text-gray-500 text-center py-8'>
                  No quizzes available yet
                </p>
              )}
            </div>
          )}

          {!isEnrolled && course.lessons && course.lessons.length === 0 && (
            <p className='text-gray-500 text-center py-8'>
              No content available yet
            </p>
          )}
        </div>
      </div>

      {/* Lesson Modal */}
      {selectedLesson && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-2xl font-bold text-gray-900'>
                  {selectedLesson.title}
                </h2>
                <button
                  onClick={() => setSelectedLesson(null)}
                  className='text-gray-400 hover:text-gray-600'
                >
                  <svg
                    className='h-6 w-6'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>

              {selectedLesson.video_url ? (
                <div className='mb-6'>
                  <div className='aspect-video bg-gray-900 rounded-lg overflow-hidden'>
                    <iframe
                      src={selectedLesson.video_url}
                      title={selectedLesson.title}
                      className='w-full h-full'
                      frameBorder='0'
                      allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              ) : (
                <div className='mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg'>
                  <p className='text-sm text-yellow-800'>
                    No video available for this lesson yet.
                  </p>
                </div>
              )}

              <div className='prose max-w-none'>
                <div className='text-gray-700 whitespace-pre-wrap'>
                  {selectedLesson.content}
                </div>
              </div>

              <div className='mt-6 flex justify-between items-center'>
                <div className='flex gap-2'>
                  <button
                    onClick={() => {
                      const currentIndex = course.lessons.findIndex(
                        (l) => l.id === selectedLesson.id,
                      )
                      if (currentIndex > 0) {
                        setSelectedLesson(course.lessons[currentIndex - 1])
                      }
                    }}
                    disabled={
                      course.lessons.findIndex(
                        (l) => l.id === selectedLesson.id,
                      ) === 0
                    }
                    className='px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    Previous Lesson
                  </button>
                  <button
                    onClick={() => {
                      const currentIndex = course.lessons.findIndex(
                        (l) => l.id === selectedLesson.id,
                      )
                      if (currentIndex < course.lessons.length - 1) {
                        setSelectedLesson(course.lessons[currentIndex + 1])
                      }
                    }}
                    disabled={
                      course.lessons.findIndex(
                        (l) => l.id === selectedLesson.id,
                      ) ===
                      course.lessons.length - 1
                    }
                    className='px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    Next Lesson
                  </button>
                </div>

                <button
                  onClick={markLessonComplete}
                  disabled={
                    loadingProgress ||
                    (userProgress &&
                      userProgress.lessons.completed_ids.includes(
                        selectedLesson.id,
                      ))
                  }
                  className={`px-6 py-2 rounded-md font-medium ${
                    userProgress &&
                    userProgress.lessons.completed_ids.includes(
                      selectedLesson.id,
                    )
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loadingProgress
                    ? 'Marking...'
                    : userProgress &&
                        userProgress.lessons.completed_ids.includes(
                          selectedLesson.id,
                        )
                      ? '✓ Completed'
                      : 'Mark Complete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CourseDetail
